'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

// Real hrefs rather than scroll handlers, so crawlers can follow the primary
// navigation and the links work without JS.
const navLinks = [
  { label: 'Services', href: '/services' },
  { label: 'Industries', href: '/industries' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Guides', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobileMenu = () => setMobileOpen(false);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-premium ${
        scrolled
          ? 'bg-background/70 backdrop-blur-xl border-b border-border-light'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-content mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        {/* Logo */}
        <Link
          href="/"
          className="flex items-center gap-2 group"
          aria-label="WeBuildPro Global home"
        >
          <Image
            src="/assets/images/wbglologo-1780491308791.png"
            alt="WeBuildPro Global logo"
            width={52}
            height={52}
            style={{ height: '52px', width: '52px', flexShrink: 0 }}
            priority
            fetchPriority="high"
          />
          <span className="font-sans font-medium text-[15px] tracking-[0.02em] text-foreground">
            WeBuildPro<span className="text-gradient"> Global</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              className="text-[13.5px] font-medium text-muted hover:text-foreground transition-colors duration-300 ease-premium relative group"
            >
              {link.label}
              <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-accent group-hover:w-full transition-all duration-300 ease-premium" />
            </Link>
          ))}
        </nav>

        {/* CTA + Mobile Toggle */}
        <div className="flex items-center gap-3">
          <Link
            href="/contact"
            className="shimmer-btn relative overflow-hidden bg-accent text-accent-foreground text-[13.5px] font-semibold px-6 py-2.5 rounded-full hover:bg-accent-hover hover:shadow-glow-gold transition-all duration-300 ease-premium hidden sm:flex items-center gap-2"
          >
            Book a Free Call
          </Link>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-foreground p-2 rounded-md hover:bg-white/5 border border-transparent hover:border-border-light transition-colors duration-300"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-t border-border-light px-6 py-8 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              onClick={closeMobileMenu}
              className="text-left text-[15px] font-medium text-muted hover:text-foreground py-4 border-b border-border-light transition-colors duration-300"
            >
              {link.label}
            </Link>
          ))}
          <Link
            href="/contact"
            onClick={closeMobileMenu}
            className="mt-6 bg-accent text-accent-foreground font-semibold px-6 py-3.5 rounded-full text-center hover:bg-accent-hover transition-colors duration-300"
          >
            Book a Free Strategy Call
          </Link>
        </div>
      )}
    </header>
  );
}